import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { Observable, of } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import { AuthService as Auth0Service } from "@auth0/auth0-angular";
import { AuthenticationService } from "src/app/services/authentication.service";

@Injectable({
    providedIn: "root",
})
export class UnauthenticatedAccessGuard {
    private auth0 = inject(Auth0Service);

    constructor(private authenticationService: AuthenticationService) {}

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // E2E test mode bypass — TestAuthHandler authenticates via header.
        if ((this.authenticationService as any).isE2EMode) {
            return of(true);
        }

        // Use Auth0's own observables as the source of truth, not our derived
        // claimsUser flag. The SDK has two relevant signals:
        //   - isLoading$: true while the SDK is initializing
        //   - isAuthenticated$: true once the SDK confirms a valid session
        //
        // The previous version waited only on isLoading$ (via guardInitObservable),
        // but isLoading$ can flip to false BEFORE user$/isAuthenticated$ emits the
        // real value. The guard then checks claimsUser between those two moments,
        // sees null, and fires login() even though the SDK has a valid token cached.
        //
        // Wait until isLoading$ is false AND then take the first isAuthenticated$
        // emission — that's the SDK's own authoritative answer.
        return this.auth0.isLoading$.pipe(
            filter((loading) => loading === false),
            take(1),
            switchMap(() => this.auth0.isAuthenticated$.pipe(take(1))),
            map((isAuthenticated) => {
                if (isAuthenticated) {
                    return true;
                }
                this.authenticationService.login(state.url);
                return false;
            })
        );
    }
}
